import { ReactElement, ReactNode } from 'react'
import { Allotment } from 'allotment'
import { Box } from '@mui/material'
import { ELEMENT_LIST_PANE_DEFAULT_WIDTH, ELEMENT_LIST_PANE_MIN_WIDTH } from './constants'

interface TwoPaneElementDrawerProps {
  /** Left pane — the element list. */
  list: ReactNode
  /** Right pane — the page's existing drawer (detail / main pane). */
  children: ReactNode
  /** Whether the element list pane is collapsed (hidden). The toggle itself lives on the
   *  scene/sidebar border in EditorSceneLayout. */
  collapsed: boolean
  /** Minimum width of the list pane (px). */
  listMinSize?: number
}

/**
 * Splits the editor's right outlet pane into an element list (left) and the page's existing drawer
 * (right), mirroring the Results2 two-pane layout. Collapsing only hides the list pane (via
 * Allotment's `visible`); the drawer expands into the freed space while the outlet keeps its width,
 * so the scene boundary doesn't move.
 */
const TwoPaneElementDrawer = ({
  list,
  children,
  collapsed,
  listMinSize = ELEMENT_LIST_PANE_MIN_WIDTH,
}: TwoPaneElementDrawerProps): ReactElement => (
  <Box sx={{ height: '100%' }}>
    <Allotment>
      <Allotment.Pane
        minSize={listMinSize}
        preferredSize={ELEMENT_LIST_PANE_DEFAULT_WIDTH}
        visible={!collapsed}
      >
        <Box height="100%">{list}</Box>
      </Allotment.Pane>
      <Allotment.Pane minSize={300}>
        <Box height="100%" overflow="auto" data-cy="element-detail-pane">
          {children}
        </Box>
      </Allotment.Pane>
    </Allotment>
  </Box>
)

export default TwoPaneElementDrawer
